import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { sipPauseResume } from "../../../api/bseApi";

function formatDateForBse(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * SipPauseResumeScreen - BSE StAR MF SIP/XSIP Pause-Resume
 * Opens from SIP List / SIP Details screen
 * Receives: ClientCode, SIP REGID, SchemeCd, Current SIP Status (Active/Paused)
 */
export default function SipPauseResumeScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const state = location.state;
  const [context, setContext] = useState(null);
  const [form, setForm] = useState({
    action: "P",
    fromDate: "",
    toDate: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const clientCode = state?.clientCode || params.get("clientCode");
    const regId = state?.regId || params.get("regId");
    const schemeCd = state?.schemeCd || params.get("schemeCd");
    const sipStatus = state?.sipStatus || params.get("sipStatus") || "Active";
    if (clientCode && regId && schemeCd) {
      setContext({
        clientCode,
        regId,
        schemeCd,
        schemeName: state?.schemeName || params.get("schemeName") || "",
        sipStatus,
      });
      return;
    }
    Swal.fire("Error", "Missing SIP details. Open from SIP List or SIP Details with ClientCode, REGID, SchemeCd.", "error").then(() => navigate("/user"));
  }, [location.state, location.search, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    if (!context?.regId) err.regId = "REGID must exist";
    if (!context?.schemeCd) err.schemeCd = "SchemeCd must exist";
    if (form.action === "P") {
      if (!form.fromDate) err.fromDate = "Pause From Date is mandatory";
      if (!form.toDate) err.toDate = "Pause To Date is mandatory";
      if (form.fromDate && form.toDate) {
        const from = new Date(form.fromDate);
        const to = new Date(form.toDate);
        if (from > to) err.toDate = "To Date must be on or after From Date";
      }
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const payload = {
        regId: context.regId,
        clientCode: context.clientCode,
        schemeCd: context.schemeCd,
        action: form.action,
      };
      if (form.action === "P") {
        payload.fromDate = formatDateForBse(form.fromDate);
        payload.toDate = formatDateForBse(form.toDate);
      }

      const res = await sipPauseResume(payload);

      if (res.data?.status === 1) {
        const msg = form.action === "P" ? "SIP paused successfully" : "SIP resumed successfully";
        Swal.fire({
          icon: "success",
          title: form.action === "P" ? "SIP Paused" : "SIP Resumed",
          text: msg,
        });
        navigate("/user");
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "SIP Pause/Resume failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const msg =
        err.response?.data?.bseMessage ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "SIP Pause/Resume failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (!context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">SIP Pause / Resume</li>
              </ol>
            </nav>
            <h5>SIP Pause / Resume</h5>
            <p className="text-muted mb-0">
              Client Code: {context.clientCode} | Status: {context.sipStatus}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Pause / Resume Details
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">SIP Registration ID (REGID)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={context.regId}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Scheme (SchemeCd)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={context.schemeName ? `${context.schemeName} (${context.schemeCd})` : context.schemeCd}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Action <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    name="action"
                    value={form.action}
                    onChange={handleChange}
                  >
                    <option value="P">Pause</option>
                    <option value="R">Resume</option>
                  </select>
                </div>
                {form.action === "P" && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">Pause From Date (DD/MM/YYYY) <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.fromDate ? "is-invalid" : ""}`}
                        name="fromDate"
                        value={form.fromDate}
                        onChange={handleChange}
                      />
                      {errors.fromDate && <div className="invalid-feedback">{errors.fromDate}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Pause To Date (DD/MM/YYYY) <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.toDate ? "is-invalid" : ""}`}
                        name="toDate"
                        value={form.toDate}
                        onChange={handleChange}
                      />
                      {errors.toDate && <div className="invalid-feedback">{errors.toDate}</div>}
                    </div>
                  </>
                )}
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      {form.action === "P" ? "Pausing..." : "Resuming..."}
                    </>
                  ) : (
                    form.action === "P" ? "Pause SIP" : "Resume SIP"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
